import type {
  LiveGatewayToolResponsePayload,
  LiveToolCall,
} from './liveTypes.js'

export type LiveToolResponseInput =
  | LiveGatewayToolResponsePayload
  | { functionResponses: unknown[] }
  | LiveGatewayToolResponsePayload['responses']
  | Record<string, unknown>
  | null
  | undefined

export function normalizeLiveToolCalls(value: unknown): LiveToolCall[] {
  if (Array.isArray(value)) {
    return value.flatMap(normalizeLiveToolCallItem)
  }

  if (isRecord(value)) {
    if (Array.isArray(value.calls)) {
      return value.calls.flatMap(normalizeLiveToolCallItem)
    }

    if (Array.isArray(value.functionCalls)) {
      return value.functionCalls.flatMap(normalizeLiveToolCallItem)
    }
  }

  return normalizeLiveToolCallItem(value)
}

export function normalizeLiveToolResponsePayload(
  value: unknown
): LiveGatewayToolResponsePayload | null {
  const responses = collectToolResponseItems(value)
  return responses.length > 0 ? { responses } : null
}

function collectToolResponseItems(
  value: unknown
): LiveGatewayToolResponsePayload['responses'] {
  if (Array.isArray(value)) {
    return value.flatMap(normalizeLiveToolResponseItem)
  }

  if (isRecord(value)) {
    if (Array.isArray(value.responses)) {
      return value.responses.flatMap(normalizeLiveToolResponseItem)
    }

    if (Array.isArray(value.functionResponses)) {
      return value.functionResponses.flatMap(normalizeLiveToolResponseItem)
    }
  }

  return normalizeLiveToolResponseItem(value)
}

function normalizeLiveToolCallItem(value: unknown): LiveToolCall[] {
  if (!isRecord(value)) return []

  const id = stringValue(value.id) || stringValue(value.toolCallId)
  const name = stringValue(value.name) || stringValue(value.toolName)
  if (!id || !name) return []

  return [
    {
      id,
      name,
      args: readRecord(value.args) || readRecord(value.input) || {},
    },
  ]
}

function normalizeLiveToolResponseItem(
  value: unknown
): LiveGatewayToolResponsePayload['responses'] {
  if (!isRecord(value)) return []

  const id = stringValue(value.id) || stringValue(value.toolCallId)
  const name = stringValue(value.name) || stringValue(value.toolName)
  if (!id || !name) return []

  return [
    {
      id,
      name,
      response: readToolResponseValue(value),
    },
  ]
}

function readToolResponseValue(value: Record<string, unknown>): unknown {
  if ('response' in value) {
    return value.response
  }

  if ('result' in value) {
    return { result: value.result }
  }

  if ('error' in value) {
    return { result: JSON.stringify({ error: value.error }) }
  }

  return {}
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}
